// Follow-up F4 — decision 11: a reception-role session signs out after 30
// minutes without activity. Owner sessions are unaffected (the caller only
// arms this for reception — see useReceptionIdleSignOut).
//
// Activity = pointer, keyboard, or the tab becoming visible again. Timers
// in a hidden tab (or a sleeping iPad) are throttled or frozen, so the idle
// test is always "how long since the last activity", checked on a short
// interval AND at the moment activity resumes — a tab that comes back after
// 45 minutes asleep signs out at once instead of treating the wake-up as
// fresh activity.
//
// Cross-tab: every tab of the portal shares one Supabase session, so an idle
// tab signing out signs out the busy desk tab too. The last activity is
// therefore shared through localStorage (throttled writes), keyed to the
// Supabase session id — a reload after 30 idle minutes still signs out,
// while a NEW sign-in never inherits the previous session's stale record.

export const RECEPTION_IDLE_MS = 30 * 60 * 1000;
export const IDLE_ACTIVITY_KEY = 'ng-last-activity';
/** sessionStorage flag the sign-in screen reads to explain the sign-out. */
export const IDLE_NOTICE_KEY = 'ng-idle-signed-out';

const CHECK_EVERY_MS = 15_000;
const SHARE_EVERY_MS = 5_000;
/** Activity closer together than this is not re-examined (pointermove fires
 * many times a second). */
const ACTIVITY_GRANULARITY_MS = 1_000;

const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown'] as const;

export interface IdleWatchOptions {
  /** Identifies the Supabase session (JWT `session_id`, else the user id). */
  sessionKey: string;
  timeoutMs?: number;
  onIdle: () => void;
  now?: () => number;
}

interface SharedActivity {
  s: string;
  at: number;
}

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function readShared(sessionKey: string): number | null {
  try {
    const raw = storage()?.getItem(IDLE_ACTIVITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SharedActivity>;
    return parsed.s === sessionKey && typeof parsed.at === 'number' && Number.isFinite(parsed.at) ? parsed.at : null;
  } catch {
    return null;
  }
}

function writeShared(sessionKey: string, at: number): void {
  try {
    storage()?.setItem(IDLE_ACTIVITY_KEY, JSON.stringify({ s: sessionKey, at } satisfies SharedActivity));
  } catch {
    // Storage blocked: this tab still times out on its own activity.
  }
}

export function clearSharedActivity(): void {
  try {
    storage()?.removeItem(IDLE_ACTIVITY_KEY);
  } catch {
    /* ignore */
  }
}

/** The Supabase session id from an access token (`session_id` claim) —
 * stable across token refreshes, new on every sign-in. */
export function sessionKeyFromToken(accessToken: string | null | undefined, fallback: string): string {
  if (!accessToken) return fallback;
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return fallback;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { session_id?: unknown };
    return typeof json.session_id === 'string' && json.session_id ? json.session_id : fallback;
  } catch {
    return fallback;
  }
}

/** Starts watching; returns a stop function. `onIdle` fires at most once. */
export function watchIdle({ sessionKey, timeoutMs = RECEPTION_IDLE_MS, onIdle, now = Date.now }: IdleWatchOptions): () => void {
  if (typeof window === 'undefined') return () => {};

  let stopped = false;
  let last: number;
  let lastWritten = 0;

  const effectiveLast = () => Math.max(last, readShared(sessionKey) ?? -Infinity);
  const expired = (t: number) => t - effectiveLast() >= timeoutMs;

  const idle = () => {
    if (stopped) return;
    stop();
    onIdle();
  };

  const record = (t: number) => {
    last = t;
    if (t - lastWritten >= SHARE_EVERY_MS) {
      lastWritten = t;
      writeShared(sessionKey, t);
    }
  };

  const onActivity = () => {
    if (stopped) return;
    const t = now();
    if (t - last < ACTIVITY_GRANULARITY_MS) return;
    if (expired(t)) {
      idle();
      return;
    }
    record(t);
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') onActivity();
  };

  // Start: a record for THIS session (another tab, or before a reload) is
  // honoured — stale means the session was already idle for too long.
  const start = now();
  const shared = readShared(sessionKey);
  if (shared !== null && start - shared >= timeoutMs) {
    // Defer, so the caller's effect has finished before it signs out.
    const t = setTimeout(() => {
      if (!stopped) {
        stopped = true;
        onIdle();
      }
    }, 0);
    return () => {
      stopped = true;
      clearTimeout(t);
    };
  }
  // Opening the page is itself activity.
  last = start;
  record(start);

  for (const type of ACTIVITY_EVENTS) window.addEventListener(type, onActivity, { passive: true, capture: true });
  document.addEventListener('visibilitychange', onVisibility);
  const interval = setInterval(() => {
    if (!stopped && expired(now())) idle();
  }, Math.min(CHECK_EVERY_MS, timeoutMs));

  function stop() {
    stopped = true;
    for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, onActivity, { capture: true });
    document.removeEventListener('visibilitychange', onVisibility);
    clearInterval(interval);
  }

  return () => {
    if (!stopped) stop();
  };
}
